/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2018
 *
 * Created by andrebarata on 2018-09-14.
 */


'use strict';

import {
  Component,
  forwardRef,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  Input,
  HostListener,
  Output,
  EventEmitter,
  OnChanges,
  ChangeDetectorRef
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Tools} from 'linkurious-shared';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide    : NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AutoCompleteComponent),
  multi      : true
};

const TEMPLATE_EXPRESSION = new RegExp(/(\/\*\*\/)([\w|\.]*)(\/\*\*\/)/g);

@Component({
  selector   : 'auto-complete',
  template: `<div class="input_wrapper">
  <div class="autocomplete_input">
    <input #inputElement
           [disabled]="disabled"
           type="text"
           class="npt npt__text"
           [autofocus]="autoFocus"
           [attr.data-selector]="selector"
           [(ngModel)]="query"
           [class.valid]="selector"
           [class.hasList]="formattedList?.length > 0 && query?.length > 0 && !selector && listVisible"
           [class.disabled]="disabled"
           [placeholder]="placeholder"
           (update)="writeValue($event.target.value)"
           (focus)="setPosition()"
           (keyup)="updateQuery($event.target.value)">
    <button *ngIf="selector && !disabled" (click)="resetQuery($event)">
      <icon name="close" size="12" stroke="8"></icon>
    </button>
  </div>
  <menu [ngStyle]="position"
        [class.none]="hideResults()"
        (close)="closeList(true)"
        class="autocomplete_list" keyboard-navigation [closeOnTop]="true">
    <ng-template [ngIf]="allowNew && query?.length > 0 && !perfectMatch">
      <menu-item (onClick)="writeNew()">
        <span class="add__new">{{ query }}</span> {{allowNewMessage || '(new)'}}
      </menu-item>
    </ng-template>
    <ng-template ngFor let-item [ngForOf]="formattedList" let-i="index">
      <menu-item (onClick)="setQuery(i, $event)">
        {{ item.label }}
      </menu-item>
    </ng-template>
  </menu>
</div>
`,
  styles: [`.input_wrapper{position:relative}.autocomplete_input input{font-size:14px;font-family:roboto,sans-serif;color:#676767;align-items:center;border-radius:3px;border:1px solid #cbcbcb;width:100%;padding:5px 30px 5px 10px;margin:5px 0;box-sizing:border-box;outline:0;transition:all ease-in-out .3s}.autocomplete_input input.valid:not(.disabled){background:#d9f1db}.autocomplete_input input.hasList{border-bottom-left-radius:0;border-bottom-right-radius:0}.autocomplete_input button{position:absolute;top:15px;right:10px;border:0;padding:0;margin:0;background:0 0;cursor:pointer}.autocomplete_input button:active,.autocomplete_input button:focus{outline:0}menu.autocomplete_list{margin:0;padding:0;background:#fff;position:fixed;top:inherit;left:3px;box-sizing:border-box;z-index:10;width:100%;max-height:240px;overflow-y:auto;box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-webkit-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-moz-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-ms-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-o-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24)}.add__new{font-style:italic;font-weight:700;margin-right:5px}`],
  providers  : [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoCompleteComponent implements ControlValueAccessor, OnChanges {

  @ViewChild('inputElement') inputElement: ElementRef;

  @Input() list: Array<any> = [];
  @Input() template: string;
  @Input() placeholder: string;
  @Input() autoFocus: boolean;
  @Input() bindingProperty: string;
  @Input() disabled: boolean;
  @Input() allowNew: boolean;
  @Input() allowNewMessage: string;
  @Input() showOnFocus: boolean = false;
  @Output() queryChange: EventEmitter<string> = new EventEmitter();

  public position = {
    top : '-9999px',
    width: '0px',
    left: '-9999px'
  };
  public formattedList: Array<{id: string, label: string}> = [];
  public perfectMatch: boolean;
  public selector: string;
  public query: string;
  public listVisible: boolean = false;
  public hasValidPosition: boolean = false;

  constructor(
    private elementRef: ElementRef,
    private _ref: ChangeDetectorRef
  ) {}

  ngOnChanges() {
    this.formatList();
    if(this.autoFocus && document.activeElement !== this.inputElement.nativeElement) {
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
        this._ref.markForCheck();
      }, 50);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent): void {
    if ( e.target === this.inputElement.nativeElement ) {
      this.listVisible = true;
      const key = e.key.toLowerCase();
      const isLetter = (key >= "a" && key <= "z");
      const isNumber = (key >= "0" && key <= "9");
      if (
        (
          (key.length === 1 && ( isLetter || isNumber ) ) ||
          e.keyCode === 8 ||
          e.keyCode === 32
        ) &&
        document.activeElement === this.inputElement.nativeElement &&
        Tools.isDefined(this.selector)
      ) {
        this.resetQuery();
      }
      if ( e.keyCode === 40 ) {
        e.preventDefault();
        e.stopPropagation();
        this.elementRef.nativeElement.getElementsByTagName('menu')[0].firstChild.getElementsByTagName('button')[0].focus();
      }
    }
  }

  /*
  * Listener on click and scroll outside of the auto complete
  */
  @HostListener('document:scroll', ['$event'])
  @HostListener('document:click', ['$event'])
  clickAndScrollEvent(event: KeyboardEvent) {
    if(!this.elementRef.nativeElement.contains(event.target)) {
      this.closeList();
    }
  }

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  /**
   * Emit the value to the parent model
   */
  writeValue(value: string): void {
    if ( this.list.length === 0 && Tools.valueExists(value) ) {
      this.queryChange.emit('');
    }
    this.query = value;
    let item = this.getListItem(value);

    if( item ) {
      this.query = this.getLabel(item);
      this.selector = this.getValue(item);
    } else {
      this.resetQuery();
    }
    this.onChangeCallback(this.selector);
  }

  /**
   * Send a new value to the parent model
   */
  writeNew(): void {
    this.selector = this.query;
    this.onChangeCallback(this.selector);
  }

  public hideResults(): boolean {
    // Hide if
    return (!this.allowNew && this.formattedList && this.formattedList.length === 0) || // cannot add new entry and list is empty
           (!this.showOnFocus && !Tools.valueExists(this.query)) || // OR cannot show on focus and query is empty
           Tools.valueExists(this.selector) || // OR an item is selected
           !this.listVisible; // OR the list is invisible
  }

  /**
   * Get selector from the selection item in list and emit it to the parent model
   */
  public setQuery(index: number, event?: MouseEvent) {
    if(Tools.isDefined(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
    if ( document.activeElement !== this.inputElement.nativeElement ) {
      this.query = this.formattedList[index].label;
      this.selector = this.getValue(this.list[index]);
      this.onChangeCallback(this.selector);
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  /**
   * Restore query and selector to the initial state
   */
  public resetQuery(e?: MouseEvent) {
    if ( !e || ( e && e.x !== 0 && e.y !== 0 ) ) {
      this.query = '';
      this.selector = undefined;
      this.onChangeCallback(this.selector);
    }
  }

  /*
  * Emit the new query value to the parent component
  */
  public updateQuery(query: string) {
    this.queryChange.emit(query);
    if(!this.hasValidPosition) {
      this.setPosition();
    }
  }

  /*
  * Close the list and put the focus on the input tag if `withFocus` is true
  */
  public closeList(withFocus?: boolean) {
    this.listVisible = false;
    if ( withFocus ) {
      this.inputElement.nativeElement.focus();
    }
  }

  /*
  * Process the correct position and size of the list
  */
  public setPosition() {
    if ( this.showOnFocus ) {
      this.listVisible = true;
      this.queryChange.emit(this.query);
    }
    let boundingRect = this.inputElement.nativeElement.getBoundingClientRect();
    this.hasValidPosition = boundingRect.bottom >= 0 && boundingRect.left >= 0;
    this.position = {
      top : Tools.numberToPixels(boundingRect.bottom),
      left: Tools.numberToPixels(boundingRect.left + 1),
      width: Tools.numberToPixels(boundingRect.width - 2)
    };
  }

  /*
  * Fill the formattedList with the list values formatted by the template string
  */
  private formatList(): void {
    this.formattedList = [];
    this.list.forEach((e) => {
      if ( this.allowNew && Tools.isDefined(this.query) ) {
        this.perfectMatch = this.getLabel(e) === this.query;
      }
      this.formattedList.push({
        id: this.getValue(e),
        label: this.getLabel(e)
      })
    });
  }

  /*
  * Return the full item from the list, searched by the bindingProperty
  */
  private getListItem(value: any): any {
    return this.list.find(item => {
      return Tools.isEqual(value, this.getValue(item));
    });
  }

  /**
   * Get the value for selector, based on the binding property
   */
  private getValue(item: any): any {
    if ( !this.bindingProperty ) {
      return item;
    }
    return Tools.getIn(item, this.bindingProperty.split('.'));
  }

  /**
   * Parse the template and return the good label for an item in list
   */
  private getLabel(e: any): string {
    if ( !this.template ) {
      return e;
    }
    return this.template.replace(
      TEMPLATE_EXPRESSION,
      ( str, ...args ) => Tools.getIn(e, args[1].split('.')) || ''
    );
  }
}

